import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getAssociationId, setAssociationId } from "@/lib/api";
import { hexToHsl } from "@/lib/format";
import { disablePush } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [aid, setAid] = useState(getAssociationId());
  const [assoc, setAssoc] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    loadMe();
    const onLogout = () => setUser(false);
    window.addEventListener("ag:logout", onLogout);
    return () => window.removeEventListener("ag:logout", onLogout);
  }, [loadMe]);

  const memberships = useMemo(() => (user && user.memberships) || [], [user]);
  const current = memberships.find((m) => m.association_id === aid) || null;

  useEffect(() => {
    if (!user) return;
    if (memberships.length && !memberships.some((m) => m.association_id === aid)) {
      const next = memberships.length === 1 ? memberships[0].association_id : null;
      setAssociationId(next);
      setAid(next);
    }
  }, [user, memberships, aid]);

  const reloadAssoc = useCallback(async () => {
    if (!current) return setAssoc(null);
    const { data } = await api.get("/association");
    setAssoc(data);
  }, [current]);

  useEffect(() => {
    reloadAssoc();
  }, [reloadAssoc]);

  useEffect(() => {
    const hex = assoc?.primary_color || "#04477E";
    document.documentElement.style.setProperty("--primary", hexToHsl(hex));
    document.documentElement.style.setProperty("--ring", hexToHsl(hex));
  }, [assoc]);

  const switchAssociation = (id) => {
    setAssociationId(id);
    setAid(id);
  };
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { login: email, password });
    if (data.memberships.length > 1) { setAssociationId(null); setAid(null); }
    setUser(data);
    return data;
  };
  const logout = async () => {
    await disablePush().catch(() => {});
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  const can = (mod, act) => !!current?.permissions?.[mod]?.includes(act);

  const value = { user, setUser, loadMe, memberships, current, assoc, reloadAssoc, switchAssociation, login, logout, can };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
